'use client';

import { PointerEvent, ReactNode, useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';
import {
  useMarqueeAnimation,
  UseMarqueeAnimationParams,
} from './useMarqueeAnimation';

export type MarqueeProps = {
  config?: UseMarqueeAnimationParams;
  gap?: number;
  repeat: number;
  children: ReactNode;
  classNames?: {
    root?: string;
    inner?: string;
  };
};

export function Marquee({
  children,
  classNames,
  gap = 0,
  repeat,
  config = {},
}: MarqueeProps) {
  const {
    x,
    playMarquee,
    pauseMarquee,
    prepareDrag,
    endDrag,
    updateDrag,
    setContentWidth,
  } = useMarqueeAnimation(config);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    prepareDrag(event.clientX);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.style.cursor = 'grabbing';
    updateDrag(event.clientX);
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    endDrag();
  };

  const onMouseEnter = () => pauseMarquee();
  const onMouseLeave = () => playMarquee();

  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (itemRef.current) {
      const width = itemRef.current.offsetWidth + gap;
      setContentWidth(width);
    }
  }, [gap, children]); // rerun if content changes

  return (
    <section
      className={cn('flex w-full overflow-hidden', classNames?.root)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {Array.from({ length: repeat }, (_, i) => (
        <div
          key={i}
          ref={i === 0 ? itemRef : undefined}
          className={cn('flex shrink-0', classNames?.inner)}
          aria-hidden={i > 0}
          style={{
            transform: `translateX(${x}px)`,
            paddingRight: `${gap}px`,
          }}
        >
          {children}
        </div>
      ))}
    </section>
  );
}
